/* VIEW — OPPORTUNITIES (list) + TOPIC / OPPORTUNITY DETAIL. Exact port of code.txt.
   List filters re-render the whole view but restore the search caret. */

import { Card, Lbl, SectionTitle, Pill, BtnNeutral, Stars, ScoreBar, SearchBar, ChipRow, ExportBar, Pager, wirePager, InferredPill, wireInferredPills, TIER_STYLE, SEARCH_LABEL_C, CONF_TEXT, StatusPill, AiTag, T, MONO, FONT, TD, fmt, escapeHtml, AioLabel, OpenLink, wireOpenLinks} from "../../ui.js";
import { searchLabel, aiLabel, pathOf, buildRecs } from "@agf/analyzer";
import { KeywordTable, MiniStat, BackBar } from "./widgets.js";

const HOW_CALC = "We compare two shares: this page's share of your site's Google Search impressions, and its share of your site's Generative AI impressions. When a page carries far more of your Search visibility than of your AI visibility, it's under-represented in AI results relative to the rest of your own site. A large gap on a topic with real search demand, real clicks, and strong rankings produces a high opportunity score. Comparing shares (rather than raw numbers) keeps the math honest even though Search Console caps exports at 1,000 rows and the Generative AI report has a shorter history.";

export function renderOpportunities(el, { R, openTopic }) {
  const st = { search: "", tier: "All", page: 0 };
  const PAGE_SIZE = 100;
  let searchTimer = null;
  const debouncedDraw = (focus) => { clearTimeout(searchTimer); searchTimer = setTimeout(() => draw(focus), 130); };

  const draw = (focusSearch = false) => {
    /* The full filtered list feeds the export (with global priorities) and the pager;
       only the current 100 cards render. */
    const filtered = R.opportunities.filter(t =>
      (st.tier === "All" || t.tier === st.tier.toUpperCase()) &&
      (!st.search || t.name.toLowerCase().includes(st.search.toLowerCase()) || (t.page || "").toLowerCase().includes(st.search.toLowerCase()))
    );
    st.page = Math.min(st.page, Math.max(0, Math.ceil(filtered.length / PAGE_SIZE) - 1));
    const base = st.page * PAGE_SIZE;
    const rows = filtered.slice(base, base + PAGE_SIZE);
    const exportData = filtered.map((t, i) => ({ priority: i + 1, page: t.page || "page not identified", topic: t.name, opportunity: t.tier, opportunity_score: t.oppScore, google_search: searchLabel(t.searchScore), search_score: t.searchScore, ai_visibility: aiLabel(t.aiScore), ai_score: t.aiScore, impressions: t.imp, clicks: t.clicks, avg_position: t.pos, related_searches: t.qCount, confidence: t.confidence, aio_shown_pct: t.aioRate == null ? "" : Math.round(t.aioRate * 100), aio_keyword_clicks: t.aioClicks == null ? "" : t.aioClicks, aio_keyword_impressions: t.aioImp == null ? "" : t.aioImp, cited_in_aio_pct: t.citedRate == null ? "" : Math.round(t.citedRate * 100) }));

    el.innerHTML = `
      <div style="margin-bottom:16px">
        <div style="font-size:15px;font-weight:800;margin-bottom:3px">Where you might be missing AI visibility</div>
        <div style="font-size:12.5px;color:${T.sub}">You already have Google visibility for these topics. Your AI visibility appears not to be keeping up — estimates to verify, not verdicts.</div>
      </div>
      <div style="display:flex;gap:10px;align-items:center;margin-bottom:14px;flex-wrap:wrap">
        ${SearchBar({ value: st.search, placeholder: "Search topics or pages…", id: "opps-search" })}
        ${ChipRow(["All", "Very High", "High", "Medium"], st.tier, "tier")}
        ${ExportBar(exportData, "ai-opportunities")}
      </div>
      ${rows.length === 0 ? Card(`<div style="text-align:center;padding:40px"><div style="font-weight:700;margin-bottom:4px">No opportunities match</div><div style="color:${T.sub};font-size:13px">Clear the search or pick a different priority.</div></div>`, { style: "text-align:center" }) : ""}
      <div style="display:flex;flex-direction:column;gap:10px">
        ${rows.map((t, i) => Card(`
          <div style="font-family:${MONO};font-weight:800;font-size:14px;color:${base + i < 3 ? T.violet : T.muted}">#${base + i + 1}</div>
          <div style="min-width:0;display:flex;flex-direction:column">
            <div style="display:flex;align-items:center;min-width:0"><span style="font-family:${MONO};font-weight:700;font-size:12.5px;color:${t.page ? T.violet : T.muted};overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(t.page ? pathOf(t.page) : "page not identified")}</span>${OpenLink(t.page, 11)}</div>
            <div style="font-size:12px;font-weight:600;color:${T.ink};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;margin-top:2px">${escapeHtml(t.name)} <span style="color:${T.muted};font-family:${MONO};font-weight:500;font-size:11px">· ${fmt(t.imp)} impr · ${fmt(t.clicks)} clicks · ${t.qCount} searches</span></div>
          </div>
          <div>${Lbl("Google Search")}${Stars(t.searchScore)}</div>
          <div>${Lbl("AI visibility", `color:${T.magenta}`)}${Pill(T.magenta, T.magentaSoft, aiLabel(t.aiScore))}</div>
          ${R.meta.hasSerp ? `
            <div>
              ${Lbl(AioLabel("AI Overview", 10), `color:${T.magenta}`)}
              ${t.aioRate != null
                ? `<div style="font-size:12px;font-weight:800;color:${T.magenta};font-family:${MONO}">~${Math.round(t.aioRate * 100)}%<span style="color:${T.muted};font-weight:600;font-size:10.5px"> · ${fmt(t.aioClicks)} clicks</span></div>`
                : `<div style="font-size:12px;font-weight:700;color:${T.muted}">not checked</div>`}
            </div>` : ""}
          <div>${Lbl("Potential")}${Pill(TIER_STYLE[t.tier].c, TIER_STYLE[t.tier].bg, t.tier)}</div>
          ${BtnNeutral("Review", { small: true, attrs: `data-open-topic="${t.id}"` })}`,
        { attrs: `data-open-topic="${t.id}" class="click-card"`, style: `padding:16px 20px;display:grid;grid-template-columns:${R.meta.hasSerp ?"36px minmax(0,2fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,0.9fr) auto" : "36px minmax(0,2fr) minmax(0,1fr) minmax(0,1fr) minmax(0,0.9fr) auto"};align-items:center;gap:14px` })).join("")}
      </div>
      ${Pager({ page: st.page, total: filtered.length, pageSize: PAGE_SIZE, noun: "opportunities" })}`;

    el.querySelector("#opps-search").addEventListener("input", e => {
      st.search = e.target.value;
      st.page = 0;
      debouncedDraw(true);
    });
    el.querySelectorAll("[data-chip-group='tier']").forEach(b => b.addEventListener("click", () => {
      st.tier = b.dataset.chipValue;
      st.page = 0;
      draw();
    }));
    wirePager(el, st.page, p => { st.page = p; draw(); });
    wireOpenLinks(el);
    el.querySelectorAll("[data-open-topic]").forEach(b => b.addEventListener("click", e => {
      e.stopPropagation();
      openTopic(b.dataset.openTopic);
    }));
    if (focusSearch) {
      const inp = el.querySelector("#opps-search");
      inp.focus();
      inp.setSelectionRange(inp.value.length, inp.value.length);
    }
  };
  draw();
}

/* ---------- TOPIC DETAIL ---------- */
export function renderTopicDetail(el, { R, topicId, onBack }) {
  const t = R.topics.find(x => x.id === topicId);
  const p = t.pageData;
  const recs = buildRecs(t, R);
  /* AI layer: when present, polished title/body replace the deterministic ones 1:1,
     keeping the original bullets (the query phrases worth covering). */
  const displayRecs = Array.isArray(t.llmRecs) && t.llmRecs.length >= recs.length
    ? recs.map((r, i) => ({ ...r, title: t.llmRecs[i].title, body: t.llmRecs[i].body }))
    : recs;
  const brief = t.llmBrief && t.llmBrief.angle ? t.llmBrief : null;
  const topQueries = [...t.queries].sort((a, b) => b.impressions - a.impressions);
  const conf = t.confidence;
  const isOpp = t.oppScore >= 50;
  const advRows = [
    ["Google impressions", fmt(t.imp)], ["Google clicks", fmt(t.clicks)], ["CTR", t.ctr + "%"], ["Average position", String(t.pos)],
    ["Related searches", String(t.qCount)],
    ["Page AI impressions", p ? fmt(p.ai.imp) : "0 (no AI-visible page)"],
    ["Share of site search impressions", ((p && p.tradShare ? p.tradShare : t.imp / R.sums.trad) * 100).toFixed(2) + "%"],
    ["Share of site AI impressions", ((p ? p.aiShare : 0) * 100).toFixed(2) + "%"],
    ["Expected AI impressions (est.)", "~" + fmt(Math.round(p ? p.expectedAi : (t.imp / R.sums.trad) * R.sums.ai))],
    ["AI visibility ratio (est., actual \u00f7 expected)", "~" + (t.aiIdx || 0).toFixed(2)],
    ...(t.aioRate != null ? [["SERP: AI Overview shown (imp-weighted)", "~" + Math.round(t.aioRate * 100) + "%"], ["SERP: cited in those AI Overviews", "~" + Math.round(t.citedRate * 100) + "%"], ["Clicks from keywords with an AI Overview", fmt(t.aioClicks)], ["Impressions from keywords with an AI Overview", fmt(t.aioImp)]] : []),
    ["Confidence", conf],
  ];

  el.innerHTML = `
    ${BackBar("Back", 'id="topic-back"')}
    <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:4px">
      <h1 style="font-size:19px;font-weight:800;letter-spacing:-0.4px;margin:0;font-family:${MONO};color:${t.page ? T.violet : T.muted}">${escapeHtml(t.page ? pathOf(t.page) : "Page not identified — it may be outside the export's top 1,000 pages")}</h1>${OpenLink(t.page, 14)}
      ${InferredPill()}
      ${Pill(TIER_STYLE[t.tier].c, TIER_STYLE[t.tier].bg, `${t.tier} POTENTIAL`)}
    </div>
    <div style="font-size:13px;font-weight:700;color:${T.ink};margin-bottom:18px">
      ${escapeHtml(t.name)}
      ${t.page ? `<span style="font-family:${MONO};font-size:11px;color:${T.muted};font-weight:500"> · ${escapeHtml(t.page)}${!R.meta.hasTradPages ? " · matched by URL" : ""}</span>` : ""}
    </div>

    <div style="display:grid;grid-template-columns:minmax(0,1.2fr) minmax(0,1fr);gap:16px;margin-bottom:16px">
      ${Card(`
        ${SectionTitle("What's happening?")}
        <p style="font-size:13px;color:${T.sub};line-height:1.6;margin:0 0 16px">
          ${isOpp
            ? "You already perform well in Google Search for this topic. But your AI visibility appears much weaker."
            : t.aiIdx >= 1.4
              ? "This topic appears to get noticeably more AI visibility than its Google Search performance alone would predict."
              : "Google Search and AI visibility appear roughly in balance for this topic."}
        </p>
        ${ScoreBar("Google Search strength", t.searchScore, T.violet)}
        ${ScoreBar("AI visibility", t.aiScore, T.magenta)}
        ${ScoreBar("AI opportunity", t.oppScore, TIER_STYLE[t.tier].c)}
        <button id="btn-calc" style="border:none;background:transparent;color:${T.violet};font-family:${FONT};font-size:11.5px;font-weight:700;cursor:pointer;padding:0">
          How is this calculated?
        </button>
        <p id="calc-text" style="display:none;font-size:12px;color:${T.sub};line-height:1.6;margin:8px 0 0;background:${T.bg};border:1px solid ${T.lineSoft};border-radius:10px;padding:10px 12px">${HOW_CALC}</p>`,
      { })}
      ${Card(`
        ${SectionTitle("Why this matters")}
        <p style="font-size:13px;color:${T.sub};line-height:1.65;margin:0">
          Your page appears for <b style="font-family:${MONO};color:${T.ink}">${t.qCount}</b> related Google searches generating approximately 
          <b style="font-family:${MONO};color:${T.ink}">${fmt(t.imp)}</b> impressions and 
          <b style="font-family:${MONO};color:${T.ink}">${fmt(t.clicks)}</b> clicks at an average position of 
          <b style="font-family:${MONO};color:${T.ink}">${t.pos}</b>.
          ${isOpp
            ? " But its estimated visibility in Generative AI results appears substantially lower than expected — likely one of your stronger AI optimization opportunities."
            : " Its estimated AI visibility is holding its own relative to that Search performance."}
        </p>
        ${t.aioRate != null ? `
          <div style="margin-top:14px;border-top:1px solid ${T.lineSoft};padding-top:12px">
            ${Lbl(`${AioLabel("Live SERP check (DataForSEO", 11)}${R.meta.serpSource === "demo" ? " · demo data" : ""})`, `color:${T.magenta}`)}
            <p style="font-size:12px;color:${T.sub};line-height:1.55;margin:6px 0 0">
              An AI Overview was shown on <b style="font-family:${MONO};color:${T.magenta}">~${Math.round(t.aioRate * 100)}%</b> of these searches (impression-weighted). 
              ${t.aioRate > 0 ? `Your site was cited in <b style="font-family:${MONO};color:${T.magenta}">~${Math.round(t.citedRate * 100)}%</b> of those AI Overviews.` : ""} 
              ${t.aioRate >= 0.5 && t.citedRate <= 0.2
                ? `<b style="color:${T.ink}">AI Overviews are live on these SERPs and you're rarely in them — that makes this opportunity concrete, not just statistical.</b>`
                : t.aioRate < 0.2
                  ? "Few of these SERPs currently show an AI Overview, so the AI upside here is smaller for now."
                  : ""}
            </p>
          </div>` : ""}
        <div style="margin-top:14px;border-top:1px solid ${T.lineSoft};padding-top:12px">
          <div style="display:flex;align-items:center;gap:8px">
            ${Lbl("Confidence", "margin-bottom:0")}
            ${Pill(conf === "HIGH" ? T.green : conf === "MEDIUM" ? T.amber : T.muted, conf === "HIGH" ? T.greenSoft : conf === "MEDIUM" ? T.amberSoft : T.lineSoft, conf)}
          </div>
          <p style="font-size:12px;color:${T.sub};line-height:1.55;margin:8px 0 0">${CONF_TEXT[conf]}</p>
        </div>`,
      { })}
    </div>

    ${SectionTitle("What we found")}
    <div style="display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,1fr);gap:12px;margin-bottom:16px">
      ${Card(`
        ${SectionTitle(`<span style="display:inline-flex;align-items:center;gap:7px"><span style="width:18px;height:18px;border-radius:6px;background:${T.violetSoft};color:${T.violet};font-family:${MONO};font-weight:800;font-size:11px;display:inline-flex;align-items:center;justify-content:center">1</span>Google Search performance</span>`)}
        <div style="display:grid;grid-template-columns:repeat(3, minmax(0,1fr));gap:8px">
          ${MiniStat("Impressions", fmt(t.imp))}
          ${MiniStat("Clicks", fmt(t.clicks))}
          ${MiniStat("CTR", t.ctr + "%")}
          ${MiniStat("Avg position", String(t.pos))}
          ${MiniStat("Related searches", String(t.qCount))}
        </div>`,
      { style: `padding:16px 18px;border-top:3px solid ${T.violet}` })}
      ${Card(`
        ${SectionTitle(`<span style="display:inline-flex;align-items:center;gap:7px"><span style="width:18px;height:18px;border-radius:6px;background:${T.magentaSoft};color:${T.magenta};font-family:${MONO};font-weight:800;font-size:11px;display:inline-flex;align-items:center;justify-content:center">2</span>${AioLabel("Generative AI", 11)}</span>`)}
        <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:8px">
          ${MiniStat("AI visibility", aiLabel(t.aiScore), { ai: true })}
          ${MiniStat("Page AI impressions", p ? fmt(p.ai.imp) : "0", { ai: true, sub: p ? null : "no AI-visible page" })}
          ${t.aioRate != null ? MiniStat("AIO on searches", "~" + Math.round(t.aioRate * 100) + "%", { ai: true }) : ""}
          ${t.aioRate != null ? MiniStat("Clicks from AIO keywords", fmt(t.aioClicks), { ai: true }) : ""}
        </div>`,
      { ai: true, style: `padding:16px 18px;border-top:3px solid ${T.magenta}` })}
    </div>

    ${KeywordTable({
      title: "Searches connected to this topic",
      queries: topQueries,
      hasSerp: R.meta.hasSerp,
      exportBase: "keywords-" + (t.page ? pathOf(t.page).replace(/[^a-z0-9]+/gi, "-") : t.id),
    })}

    ${brief ? `
      ${SectionTitle(AioLabel("Content brief", 11))}
      ${Card(`
        <div style="font-size:13px;color:${T.sub};line-height:1.6;margin:0 0 14px">${escapeHtml(brief.angle)}</div>
        ${brief.outline.length ? `
          ${Lbl("Suggested outline")}
          <div style="display:flex;flex-direction:column;gap:7px;margin-bottom:14px">
            ${brief.outline.map((h, i) => `
              <div style="display:flex;gap:10px;align-items:flex-start">
                <span style="width:18px;height:18px;border-radius:6px;background:${T.violetSoft};color:${T.violet};font-family:${MONO};font-weight:800;font-size:11px;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px">${i + 1}</span>
                <span style="font-size:12.5px;color:${T.ink};font-weight:600;line-height:1.5">${escapeHtml(h)}</span>
              </div>`).join("")}
          </div>` : ""}
        ${brief.questions.length ? `
          ${Lbl("Questions this page should answer")}
          <div style="display:flex;flex-wrap:wrap;gap:6px">
            ${brief.questions.map(q => Pill(T.sub, T.lineSoft, escapeHtml(q))).join("")}
          </div>` : ""}`,
      { ai: true, style: "margin-bottom:20px" })}` : ""}

    ${SectionTitle("What could help?", { right: t.llmRecs ? AioLabel("AI-polished", 10) : null })}
    <div style="display:flex;flex-direction:column;gap:12px;margin-bottom:20px">
      ${displayRecs.map((r, i) => Card(`
        <div style="display:flex;gap:12px">
          <div style="width:26px;height:26px;border-radius:8px;background:${T.violetSoft};color:${T.violet};font-family:${MONO};font-weight:800;font-size:12.5px;display:flex;align-items:center;justify-content:center;flex-shrink:0">${i + 1}</div>
          <div style="flex:1">
            <div style="font-weight:800;font-size:13.5px;margin-bottom:4px">${escapeHtml(r.title)}</div>
            <div style="font-size:12.5px;color:${T.sub};line-height:1.55">${escapeHtml(r.body)}</div>
            ${r.bullets.length > 0 ? `
              <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:10px">
                ${r.bullets.map(b => Pill(T.sub, T.lineSoft, escapeHtml(b), `font-weight:600;font-family:${b.startsWith("/") ? MONO : FONT}`)).join("")}
              </div>` : ""}
          </div>
        </div>`,
      { style: "padding:18px 20px" })).join("")}
    </div>

    <button id="btn-details" style="border:1px solid ${T.line};background:${T.surface};color:${T.sub};font-family:${FONT};font-size:12px;font-weight:700;cursor:pointer;border-radius:9px;padding:7px 14px;margin-bottom:12px">View details</button>
    <div id="details-box" style="display:none">
      ${Card(`
        ${SectionTitle("Advanced metrics")}
        <table style="width:100%;border-collapse:collapse">
          <tbody>
            ${advRows.map(([k, v]) => `<tr>${TD(k, { style: `color:${T.sub}` })}${TD(v, { mono: true, style: "text-align:right" })}</tr>`).join("")}
          </tbody>
        </table>
        <div style="font-size:11px;color:${T.muted};margin-top:10px">Expected AI impressions = this page's share of your site's Search impressions × your site's total AI impressions. Topic↔page↔AI relationships are inferred from your URLs — Google does not provide the query behind AI impressions.</div>`,
      { style: "margin-bottom:8px" })}
    </div>`;

  /* wiring */
  el.querySelector("#topic-back").addEventListener("click", onBack);
  el.querySelector("#btn-calc").addEventListener("click", () => {
    const btn = el.querySelector("#btn-calc");
    const box = el.querySelector("#calc-text");
    const open = box.style.display !== "none";
    box.style.display = open ? "none" : "block";
    btn.textContent = open ? "How is this calculated?" : "Hide calculation";
  });
  el.querySelector("#btn-details").addEventListener("click", () => {
    const btn = el.querySelector("#btn-details");
    const box = el.querySelector("#details-box");
    const open = box.style.display !== "none";
    box.style.display = open ? "none" : "block";
    btn.textContent = open ? "View details" : "Hide details";
  });
  wireInferredPills(el);
  wireOpenLinks(el);
}
